import React from "react";
import {
  TextField,
  Grid,
  Box,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createWard, updateWard } from "../../services/WardService.js";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../utils/formModalStyles";

const validationSchema = Yup.object({
  name: Yup.string().required("Ward name is required"),
  floor: Yup.number()
    .typeError("Ward floor must be a number")
    .required("Ward floor is required")
    .integer("Ward floor must be an integer"),
  wardType: Yup.string().required("Ward type is required"),
  totalBeds: Yup.number()
    .typeError("Total beds must be a number")
    .required("Total beds is required")
    .positive("Total beds must be positive")
    .integer("Total beds must be an integer"),
});

const AddWardModal = ({ open, onClose, wardInfo }) => {
  const isEditMode = Boolean(wardInfo?.id);

  const formik = useFormik({
    initialValues: {
      name: wardInfo?.name || "",
      floor: wardInfo?.floor?.toString() || "",
      wardType: wardInfo?.wardType || "",
      totalBeds: wardInfo?.totalBeds?.toString() || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          name: values.name,
          floor: Number(values.floor),
          wardType:
            values.wardType.charAt(0).toUpperCase() + values.wardType.slice(1),
          totalBeds: Number(values.totalBeds),
        };

        if (isEditMode) {
          await updateWard(wardInfo.id, payload);
          alert("Ward updated successfully");
        } else {
          await createWard(payload);
          alert("Ward created successfully");
        }
        resetForm();
        onClose();
      } catch (error) {
        console.error("Failed to save ward!", error);
        alert("Failed to save ward.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <BasicModal
      open={open}
      onClose={() => {
        formik.resetForm();
        onClose();
      }}
      title={isEditMode ? "Edit Ward" : "Add New Ward"}
    >
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Ward Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="floor"
                name="floor"
                label="Ward Floor"
                value={formik.values.floor}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.floor && Boolean(formik.errors.floor)}
                helperText={formik.touched.floor && formik.errors.floor}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="wardType"
                name="wardType"
                label="Ward Type"
                value={formik.values.wardType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.wardType && Boolean(formik.errors.wardType)}
                helperText={formik.touched.wardType && formik.errors.wardType}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="totalBeds"
                name="totalBeds"
                label="Total Beds"
                type="number"
                value={formik.values.totalBeds}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.totalBeds && Boolean(formik.errors.totalBeds)}
                helperText={formik.touched.totalBeds && formik.errors.totalBeds}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={() => {
                  formik.resetForm();
                  onClose();
                }}
                submitType="submit"
                submitLabel={isEditMode ? "Update Ward" : "Create Ward"}
                submitDisabled={formik.isSubmitting}
                loading={formik.isSubmitting}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};
export default AddWardModal;
